import React, { useRef, useState, useEffect, useCallback, memo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLocation } from 'react-router-dom';
import screenfull from 'screenfull';
import HTMLFlipBook from 'react-pageflip';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import PDFLoader from './PDFLoader';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FlipPage = memo(({ pageNumber, scale }) => (
    <div className="mb-4 text-center">
        <Page
            pageNumber={pageNumber}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            scale={scale}
        />
        <div className="mt-2 text-sm text-muted">Page {pageNumber}</div>
    </div>
));

FlipPage.displayName = 'FlipPage';

const ScrollBook = memo(({ numPages, scale, onVisiblePageChange }) => {
    const observer = useRef(null);
    useEffect(() => {
        const options = { root: null, rootMargin: '0px', threshold: 0.5 };
        observer.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const match = entry.target.id.match(/pdf_page_(\d+)/);
                    if (match) onVisiblePageChange(parseInt(match[1]));
                }
            });
        }, options);

        const targets = document.querySelectorAll('[id^="pdf_page_"]');
        targets.forEach((el) => observer.current.observe(el));

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [numPages, onVisiblePageChange]);

    return (
        <div className="d-flex justify-content-center scroll-view" style={{ minHeight: '100vh' }}>
            <div className="flex flex-col gap-6 items-center">
                {Array.from({ length: numPages }, (_, index) => (
                    <div key={index} id={`pdf_page_${index + 1}`}>
                        <FlipPage pageNumber={index + 1} scale={scale} />
                    </div>
                ))}
            </div>
        </div>
    );
});
ScrollBook.displayName = 'ScrollBook';

const ThumbnailView = memo(({ numPages }) => (
    <div className='container'>
        <div className="row" style={{ minHeight: '80vh' }}>
            {Array.from({ length: numPages }, (_, index) => (

                <div key={`thumb_${index + 1}`} className="col-md-3 col-6 mb-4 thumnail-view">
                    <Page
                        pageNumber={index + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={0.5}
                    />
                    <div className="text-center text-sm mt-2">{index + 1}</div>
                </div>

            ))}
        </div>
    </div>
));
ThumbnailView.displayName = 'ThumbnailView';

const flipbookProps = {
    width: 550,
    height: 700,
    size: "stretch",
    minWidth: 315,
    maxWidth: 1000,
    minHeight: 400,
    maxHeight: 1533,
    maxShadowOpacity: 0.5,
    showCover: true,
    mobileScrollSupport: true,
};

export default function PDFViewer() {
    const [scale, setScale] = useState(1.0);
    const [numPages, setNumPages] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isFlipMode, setIsFlipMode] = useState(false);
    const [isThumbnailMode, setIsThumbnailMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchPage, setSearchPage] = useState('');
    const flipBookRef = useRef(null);
    const divRef = useRef(null);

    const location = useLocation();
    /* const navigate = useNavigate(); */
    const message = location?.state?.message;

    /*   const goBack = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          alert('No history available to go back.');
        }
      }; */

    const toggleFullscreen = () => {
        if (screenfull.isEnabled) {
            screenfull.toggle(divRef.current);
        }
    };

    const handleFullscreenChange = useCallback(() => {
        setIsFullscreen(!!screenfull.isFullscreen);
    }, []);

    useEffect(() => {
        if (screenfull.isEnabled) {
            screenfull.on('change', handleFullscreenChange);
            return () => screenfull.off('change', handleFullscreenChange);
        }
    }, [handleFullscreenChange]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const zoomIn = () => setScale(prev => (prev < 2.0 ? prev + 0.2 : prev));
    const zoomOut = () => setScale(prev => (prev > 0.5 ? prev - 0.2 : prev));
    const resetZoom = () => setScale(1.0);

    const goToPage = () => {
        const page = parseInt(searchPage);
        if (isNaN(page) || page < 1 || page > numPages) return;
        setCurrentPage(page);
        if (isFlipMode && flipBookRef.current) {
            flipBookRef.current.pageFlip().flip(page - 1);
        } else if (!isFlipMode && !isThumbnailMode) {
            const el = document.getElementById(`pdf_page_${page}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };
    const formatDate = (date) => {
        const offset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(date.getTime() + offset);
        return istDate.toISOString().replace('T', ' ').split('.')[0];
    };
    const baseURL = process.env.REACT_APP_BASE_URL;
    const apikey = process.env.REACT_APP_API_KEY;
    const bookid = location?.state?.id;
    const user_id = localStorage.getItem('userStatID');

    useEffect(() => {

        const startTime = new Date();

        return () => {
            const endTime = new Date();
            const reading_seconds = Math.floor((endTime - startTime) / 1000);

            const payload = [
                {
                    bookid,
                    user_id,
                    starttime: formatDate(startTime),
                    endtime: formatDate(endTime),
                    reading_seconds
                }
            ];

            fetch(`${baseURL}/insert_reading_sessions/`, {
                method: 'POST',
                headers: {
                    'Authorization': apikey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })
                .then(async response => {
                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error(`HTTP error! Status: ${response.status}, Response: ${text}`);
                    }
                    return response.json();
                })
                .catch(error => {
                    console.error('Error sending reading session:', error);
                });
        };
    }, [apikey, baseURL, bookid, user_id]);

    if (!message) return <div>No PDF file provided.</div>;

    return (
        <section ref={divRef} className={`pdf-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
            <div className="pdf_head">
                <div className="row">
                    <div className="col-md-12 text-start position-relative pdf-doc">
                        <Document file={message} onLoadSuccess={onDocumentLoadSuccess} loading={<PDFLoader />}>
                            {numPages && (
                                isThumbnailMode ? (
                                    <ThumbnailView numPages={numPages} />
                                ) : isFlipMode ? (
                                    <div className='container'>
                                        <div className="flex justify-center book-view mx-3">
                                            <HTMLFlipBook
                                                {...flipbookProps}
                                                ref={flipBookRef}
                                                onFlip={({ data }) => setCurrentPage(data + 1)}
                                            >
                                                {Array.from({ length: numPages }, (_, index) => (
                                                    <div className="page" key={`page_${index + 1}`}>
                                                        <Page
                                                            pageNumber={index + 1}
                                                            renderTextLayer={false}
                                                            renderAnnotationLayer={false}
                                                            scale={scale}
                                                        />
                                                    </div>
                                                ))}
                                            </HTMLFlipBook>
                                        </div>
                                    </div>
                                ) : (
                                    <ScrollBook numPages={numPages} scale={scale} onVisiblePageChange={setCurrentPage} />
                                )
                            )}


                            <div className="d-flex align-items-center btn_slider_style">
                                <div className='slide_btn'>
                                    {numPages && (

                                        <>
                                            <input type="range" min="1"
                                                max={numPages}
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = parseInt(e.target.value);
                                                    setCurrentPage(newPage);
                                                    if (isFlipMode && flipBookRef.current) {
                                                        flipBookRef.current.pageFlip().flip(newPage - 1);
                                                    } else if (!isFlipMode && !isThumbnailMode) {
                                                        const el = document.getElementById(`pdf_page_${newPage}`);
                                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    }
                                                }}
                                                className="form-range page-slider me-3" />
                                            <button className="btn f_increase view-all-btnn me-2" onClick={() => {
                                                const newPage = Math.max(1, currentPage - 1);
                                                setCurrentPage(newPage);
                                                if (isFlipMode && flipBookRef.current) {
                                                    flipBookRef.current.pageFlip().flip(newPage - 1);
                                                } else if (!isFlipMode && !isThumbnailMode) {
                                                    const el = document.getElementById(`pdf_page_${newPage}`);
                                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }
                                            }}>
                                                <i className="fas fa-chevron-left"></i>
                                            </button>


                                            <button className="btn f_increase view-all-btnn me-2" onClick={() => {
                                                const newPage = Math.min(numPages, currentPage + 1);
                                                setCurrentPage(newPage);
                                                if (isFlipMode && flipBookRef.current) {
                                                    flipBookRef.current.pageFlip().flip(newPage - 1);
                                                } else if (!isFlipMode && !isThumbnailMode) {
                                                    const el = document.getElementById(`pdf_page_${newPage}`);
                                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                }
                                            }}>
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className='all_btn'>
                                    <input type="number" value={searchPage} onChange={(e) => setSearchPage(e.target.value)} className="form-control form-control-sm me-2 go_to_page" />
                                    <button className="btn f_increase view-all-btnn" onClick={goToPage}>Go</button>
                                    <button className="btn f_increase view-all-btnn me-2" onClick={zoomIn}><i className="fas fa-search-plus"></i></button>
                                    <button className="btn f_increase view-all-btnn me-2" onClick={resetZoom}><i className="fas fa-redo-alt"></i></button>
                                    <button className="btn f_increase view-all-btnn me-2" onClick={zoomOut}><i className="fas fa-search-minus"></i></button>
                                    <button className="btn f_increase view-all-btnn me-2" onClick={toggleFullscreen}>
                                        <i className={isFullscreen ? "fa fa-compress" : "fa fa-expand"}></i>
                                    </button>

                                    <button className={`btn f_increase view-all-btnn me-2 ${!isFlipMode && !isThumbnailMode ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setIsFlipMode(false); setIsThumbnailMode(false); }}>
                                        <i className="fas fa-scroll"></i>
                                    </button>
                                    <button className={`btn f_increase view-all-btnn me-2 ${isFlipMode ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setIsFlipMode(true); setIsThumbnailMode(false); }}>
                                        <i className="fas fa-book-open"></i>
                                    </button>
                                    <button className={`btn f_increase view-all-btnn me-2 ${isThumbnailMode ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setIsFlipMode(false); setIsThumbnailMode(true); }}>
                                        <img className=' thumbnail-img' src={`${process.env.PUBLIC_URL}/assets/images/elements/thumbnail.png`} alt="thumbnail" />
                                    </button>

                                    <span className="mx-3 fw-bold d-none">Page {currentPage} of {numPages || '--'}</span>
                                </div>

                            </div>
                        </Document>
                    </div>
                </div>
            </div>
        </section>
    );
}
